import { FC, useCallback, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
// component
import InputController from 'controller/InputController';
// constants, interface, schema
import {
  CANCEL_TEXT,
  DISCARD_AND_LEAVE,
  FORMULA_NAME_TEXT,
  UNSAVED_FORMULA_TEXT,
  UNSAVED_FORMULA_MESSAGE,
  SAVE_DRAFT_AND_LEAVE_TEXT,
  saveFormulaAsDraftFormInitialValue,
} from 'constants/index';
import { saveFormulaAsDraftSchema } from 'validationSchema';
import { forInputLabelBg } from 'styles/commonComponentStyle';
import { DialogBoxProps, SaveFormulaAsDraftFormType } from 'interfaces';

const DialogBox: FC<DialogBoxProps> = ({
  showDialog,
  formulaName,
  cancelNavigation,
  confirmNavigation,
  saveAndLeaveNavigation: submitHandler,
}) => {
  const methods = useForm<SaveFormulaAsDraftFormType>({
    defaultValues: saveFormulaAsDraftFormInitialValue,
    resolver: yupResolver(saveFormulaAsDraftSchema),
  });

  const { handleSubmit, setValue, clearErrors } = methods;

  const onSubmit: SubmitHandler<SaveFormulaAsDraftFormType> = (data) => {
    submitHandler && submitHandler(data);
  };

  const setValues = useCallback(() => {
    setValue('name', formulaName ?? '');
  }, [formulaName, setValue]);

  useEffect(() => {
    formulaName && setValues();
  }, [formulaName, setValues]);

  return (
    <FormProvider {...methods}>
      <Dialog open={showDialog} maxWidth={'md'} sx={{ padding: 5 }}>
        <DialogTitle>{UNSAVED_FORMULA_TEXT}</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Typography variant="caption" fontSize={16} fontWeight={500}>
            {UNSAVED_FORMULA_MESSAGE}
          </Typography>
          <Box sx={[forInputLabelBg, { mt: 2 }]}>
            <InputController isRequired name="name" title={FORMULA_NAME_TEXT} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display={'flex'} flexWrap={'wrap'}>
            <Button
              size="small"
              sx={{ mr: 2 }}
              color="primary"
              variant="contained"
              onClick={handleSubmit(onSubmit)}>
              {SAVE_DRAFT_AND_LEAVE_TEXT}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={confirmNavigation}
              size="small"
              sx={{ mr: 2 }}>
              {DISCARD_AND_LEAVE}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                clearErrors();
                cancelNavigation && cancelNavigation();
              }}
              size="small">
              {CANCEL_TEXT}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default DialogBox;
